import { useCallback, useEffect, useState } from 'react';
import { apiService } from './../services/apiService';
import { trackClick } from './../utils/analytics';

/**
 * What this account may do, and one shared place to raise the upgrade modal
 * when it may not.
 *
 * Entitlements are decided server-side (`GET /api/v1/entitlements`) rather
 * than inferred from the plan in the browser: the lock is an admin switch,
 * and a client that decides its own permissions is a paywall in name only.
 *
 * Both the entitlements and the "which feature got blocked" flag live in a
 * module-level store rather than per-hook state. Several components call this
 * (the tech card, saved cards, chat results, Direct Search) but only ONE
 * PricingModal is mounted — with per-instance state, a lock raised in a card
 * would set a flag nobody renders, and the click would do nothing at all.
 * The shared store also means entitlements are fetched once, not per caller.
 */

export interface Entitlements {
  paid: boolean;
  lock_enabled: boolean;
  can_filter: boolean;
  can_search: boolean;
  can_contact: boolean;
  preview_results: number | null;
  plan?: string;
  /** The server's billing lookup failed: not an answer. The route sends it
   *  as success:false; a body that still says `degraded` takes the same
   *  loadFailed path rather than being cached as this account's plan. */
  degraded?: boolean;
}

/** Signed out: nothing to gate in the shell, and the SERVER still caps
 *  anonymous searches, so this is a rendering default rather than a grant. */
const OPEN: Entitlements = {
  paid: true, lock_enabled: false, can_filter: true, can_search: true,
  can_contact: true, preview_results: null,
};

/** Entitlements unreachable while SIGNED IN. Falling back to OPEN here would
 *  hand every locked feature to anyone whose lookup 500s or times out — the
 *  server would still refuse, so the user would get a broken action instead
 *  of the upgrade prompt that explains it. */
const CLOSED: Entitlements = {
  paid: false, lock_enabled: true, can_filter: false, can_search: false,
  can_contact: false, preview_results: 2,
};

type Listener = () => void;

const store = {
  ent: null as Entitlements | null,
  // WHO the cached entitlements belong to. Without this the cache outlives
  // the session: sign out, sign in as someone else, and the previous
  // account's permissions (or the signed-out OPEN default) are still in
  // force, because the loader short-circuits on `ent` already being set.
  entUserId: null as string | null,
  // True when the LAST load errored rather than returning a real answer. The
  // CLOSED fallback is byte-identical to a genuine free-tier response, so
  // without this the UI cannot tell "you must upgrade" from "we could not
  // check" — and a paying subscriber gets shown an upgrade paywall.
  loadFailed: false,
  lockedFeature: null as string | null,
  inFlight: null as Promise<void> | null,
  listeners: new Set<Listener>(),
};

const emit = () => store.listeners.forEach(l => l());

const loadEntitlements = (): Promise<void> => {
  const userId = apiService.getStoredUserId() || null;
  if (store.ent && store.entUserId === userId) return Promise.resolve();
  if (store.inFlight) return store.inFlight;
  if (!userId) {
    store.ent = OPEN;
    store.entUserId = null;
    emit();
    return Promise.resolve();
  }
  store.inFlight = apiService.getEntitlements(userId)
    .then(res => {
      // `degraded`: the server could not read the billing state, so "not
      // paid" in that body means "unknown" — a paying subscriber must not be
      // shown the Explorer landing or a locked search box for the session.
      if (res.success && res.data?.success && !res.data?.degraded) {
        store.ent = res.data as any;
        store.entUserId = userId;          // cache the ANSWER
        store.loadFailed = false;
      } else {
        // A failure here is almost always "the access token has not been
        // restored into localStorage yet" — the first few calls after a reload
        // 401 and then start succeeding. Render locked, but do NOT cache it
        // against this user: caching the failure is what left a paying account
        // showing "Full filtering is a paid feature" for the entire session,
        // with no retry, while the Billing tab (which refetches) correctly
        // showed their plan.
        store.ent = CLOSED;
        store.entUserId = null;            // leave it stale so the next mount retries
        store.loadFailed = true;           // NOT the same as "free tier"
      }
    })
    .catch(() => { store.ent = CLOSED; store.entUserId = null; store.loadFailed = true; })
    .finally(() => { store.inFlight = null; emit(); });
  return store.inFlight;
};

/** Fired by refreshEntitlements: the account's plan may have changed (a
 *  payment or a trial started, e.g. on the Billing tab). App re-reads the
 *  billing summary on it, so the upgrade prompts step aside at once. */
export const BILLING_CHANGED_EVENT = 'utech:billing-changed';

/** Re-read entitlements — call after a successful payment or a sign-in. */
export const refreshEntitlements = () => {
  store.ent = null;
  store.entUserId = null;
  try { window.dispatchEvent(new Event(BILLING_CHANGED_EVENT)); } catch { /* no window */ }
  return loadEntitlements();
};

export function useUpgradeGate() {
  const [, forceRender] = useState(0);

  useEffect(() => {
    const listener = () => forceRender(n => n + 1);
    store.listeners.add(listener);
    loadEntitlements();
    return () => { store.listeners.delete(listener); };
  }, []);

  /**
   * Raise the paywall and record it. Every blocked interaction — a locked
   * filter, a locked search, a locked contact — funnels through here, so this
   * one event answers "which lock do free users hit, and how often" without
   * needing a separate call at each of the ~10 gate sites (which is how call
   * sites get missed).
   */
  const raiseLock = (feature: string, capability?: keyof Entitlements,
                     ctx?: Record<string, unknown>) => {
    // `ctx` says which control was hit (search_bar / filter / contact) and
    // where (surface, tech_id, trigger). It goes first so it can never
    // overwrite the three keys every existing report reads.
    trackClick('paywall_shown', {
      ...(ctx || {}),
      feature,
      capability: capability || null,
      plan: store.ent?.plan || null,
    });
    store.lockedFeature = feature;
    emit();
  };

  /**
   * Run `action` if allowed, otherwise raise the pricing modal naming the
   * feature. Returns true when the action ran.
   */
  const gate = useCallback((allowed: boolean, feature: string, action?: () => void,
                            capability?: keyof Entitlements,
                            ctx?: Record<string, unknown>) => {
    // Not loaded yet: settle the lookup before deciding. The permissive
    // default exists so the UI can render, not so a click in the first 200ms
    // walks through the paywall. Re-deciding uses the CAPABILITY key, not a
    // match on the display string — a renamed label used to silently fall
    // back to the caller's own `allowed`, which is the value we distrust.
    if (store.ent === null) {
      loadEntitlements().then(() => {
        const now = store.ent || CLOSED;
        const stillAllowed = capability ? Boolean(now[capability]) : allowed;
        if (stillAllowed || store.loadFailed) action?.();
        else { raiseLock(feature, capability, ctx); }
      });
      return false;
    }
    if (allowed) { action?.(); return true; }
    // Never show an upgrade prompt off the back of a failed lookup: that is how
    // a Voyager subscriber ended up staring at "Unlock search". Re-ask in the
    // background and let this one through — every gated capability is enforced
    // server-side too, so this cannot hand out access we did not grant.
    if (store.loadFailed) {
      loadEntitlements();
      action?.();
      return true;
    }
    raiseLock(feature, capability, ctx);
    return false;
  }, []);

  /** Open the plans sheet with nothing locked behind it — an automatic
   *  upgrade prompt, not a blocked action — so no paywall_shown: the checkout
   *  funnel counts people who hit a lock or chose to look, not everyone who
   *  signed in. `feature` still labels the sheet's own clicks. Returns false
   *  (and opens nothing) while the entitlements lookup is failing, the same
   *  rule gate() follows. */
  const openPlans = useCallback((feature: string): boolean => {
    if (store.loadFailed) return false;
    store.lockedFeature = feature;
    emit();
    return true;
  }, []);

  const closeUpgrade = useCallback(() => {
    store.lockedFeature = null;
    emit();
  }, []);

  /** Re-ask the server. Used after sign-in/session-restore, and after a
   *  successful upgrade, so the locks lift without a page reload. */
  const refresh = useCallback(() => {
    store.entUserId = null;
    return loadEntitlements();
  }, []);

  return {
    ent: store.ent || OPEN,
    ready: store.ent !== null,
    /** The last entitlements lookup errored (`ent` is the CLOSED fallback,
     *  not a real free-tier answer). Layouts that only make sense for a
     *  genuine Explorer — the Cosmos picks view, the search lock — must
     *  check this so a paying user is never shown them. */
    loadFailed: store.loadFailed,
    lockedFeature: store.lockedFeature,
    closeUpgrade,
    gate,
    openPlans,
    refresh,
  };
}
